import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

export interface PlayerPlacementItem {
  id: string | number;
  name: string;
  position?: string;
  salary?: number;
}

interface TradePlacementModalProps {
  show: boolean;
  players: PlayerPlacementItem[];
  language: string;
  theme?: string;
  /** Optional custom title override */
  title?: string;
  /** True = proposant submitting, False/undefined = acceptant confirming */
  isProposer?: boolean;
  onConfirm: (placements: Record<string, 'main' | 'reserve'>) => void;
  onCancel: () => void;
}

export default function TradePlacementModal({
  show,
  players,
  language,
  theme = 'light',
  title,
  isProposer = true,
  onConfirm,
  onCancel,
}: TradePlacementModalProps) {
  const [placements, setPlacements] = useState<Record<string, 'main' | 'reserve'>>({});

  // Initialize all players to 'main' each time the modal opens with a new set of players
  useEffect(() => {
    if (show) {
      const initial: Record<string, 'main' | 'reserve'> = {};
      players.forEach(p => {
        initial[String(p.id)] = 'main';
      });
      setPlacements(initial);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show, players]);

  if (!show) return null;

  const isDark = theme === 'dark';

  const formatCurrency = (amount?: number) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact',
    }).format(amount);
  };

  const modalTitle =
    title ||
    (language === 'fr'
      ? 'Placement des joueurs reçus'
      : 'Placement of Received Players');

  const helpText =
    language === 'fr'
      ? isProposer
        ? "Si l'échange est accepté, où souhaitez-vous placer chaque joueur que vous recevrez?"
        : "Où souhaitez-vous placer chaque joueur que vous recevez dans cet échange?"
      : isProposer
      ? 'If the trade is accepted, where do you want to place each player you will receive?'
      : 'Where do you want to place each player you are receiving in this trade?';

  const confirmLabel =
    language === 'fr'
      ? isProposer
        ? "Confirmer et soumettre l'échange"
        : "Confirmer et accepter l'échange"
      : isProposer
      ? 'Confirm & Submit Trade'
      : 'Confirm & Accept Trade';

  return ReactDOM.createPortal(
    <>
      <div
        className="modal fade show"
        style={{
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1055,
          overflowY: 'auto',
        }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className={`modal-content border-0 shadow-lg ${isDark ? 'bg-dark text-white' : ''}`}
          >
            {/* Header */}
            <div className={`modal-header ${isDark ? 'border-secondary' : ''}`}>
              <h5 className="modal-title fw-bold">
                🏒 {modalTitle}
              </h5>
              <button
                type="button"
                className="btn-close"
                style={isDark ? { filter: 'invert(1)' } : {}}
                onClick={onCancel}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body">
              <p className={`small mb-3 ${isDark ? 'text-white-50' : 'text-muted'}`}>
                {helpText}
              </p>

              {players.length === 0 ? (
                <p className="text-muted text-center py-3">
                  {language === 'fr' ? 'Aucun joueur à placer.' : 'No players to place.'}
                </p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {players.map(player => {
                    const key = String(player.id);
                    const current = placements[key] ?? 'main';

                    return (
                      <div
                        key={key}
                        className={`p-2 rounded border ${
                          isDark ? 'border-secondary' : 'border-light bg-light'
                        }`}
                      >
                        {/* Player info */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <div
                              className={`fw-semibold small ${
                                isDark ? 'text-white' : 'text-dark'
                              }`}
                            >
                              {player.name}
                            </div>
                            {(player.position || player.salary) && (
                              <div
                                className="text-muted"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {player.position && <span>{player.position}</span>}
                                {player.position && player.salary && (
                                  <span> • </span>
                                )}
                                {player.salary && (
                                  <span>{formatCurrency(player.salary)}</span>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Current selection badge */}
                          <span
                            className={`badge rounded-pill ${
                              current === 'main' ? 'bg-success' : 'bg-info'
                            }`}
                            style={{ fontSize: '0.7rem' }}
                          >
                            {current === 'main'
                              ? language === 'fr'
                                ? 'Club Principal'
                                : 'Main Club'
                              : language === 'fr'
                              ? 'Réserves'
                              : 'Reserves'}
                          </span>
                        </div>

                        {/* Toggle buttons */}
                        <div className="btn-group w-100" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              current === 'main'
                                ? 'btn-success'
                                : isDark
                                ? 'btn-outline-secondary'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() =>
                              setPlacements(prev => ({ ...prev, [key]: 'main' }))
                            }
                          >
                            🏒 {language === 'fr' ? 'Club Principal' : 'Main Club'}
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              current === 'reserve'
                                ? 'btn-info'
                                : isDark
                                ? 'btn-outline-secondary'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() =>
                              setPlacements(prev => ({ ...prev, [key]: 'reserve' }))
                            }
                          >
                            🥅 {language === 'fr' ? 'Réserves' : 'Reserves'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`modal-footer ${isDark ? 'border-secondary' : ''}`}>
              <button
                type="button"
                className={`btn btn-sm ${
                  isDark ? 'btn-outline-light' : 'btn-outline-secondary'
                }`}
                onClick={onCancel}
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onConfirm(placements)}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
        }}
        onClick={onCancel}
      />
    </>,
    document.body
  );
}
